
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    makeStyles,
    useTheme,
    IconButton
} from '@material-ui/core';
import { ExpandMore, Close } from '@material-ui/icons';
import { i18n } from "../../translate/i18n";
import { changes } from "./changes";
import "./ChangesLog.css";

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        paddingTop: '10px'
    },
    scrollArea: {
        ...theme.scrollbarStyles,
        overflowY: "auto",
        maxHeight: "65vh",
        paddingRight: theme.spacing(1),
    },
    heading: {
        fontSize: theme.typography.pxToRem(16),
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        flexBasis: '40%',
        flexShrink: 0,
        color: theme.palette.type === 'dark' ? '#f8fafc' : '#0F172A',
    },
    secondaryHeading: {
        fontSize: theme.typography.pxToRem(14),
        color: theme.palette.type === 'dark' ? '#94a3b8' : '#64748B',
        display: 'flex',
        alignItems: 'center',
    },
    content: {
        whiteSpace: 'pre-line', // Preserve newlines
        fontSize: '14px',
        color: theme.palette.type === 'dark' ? '#cbd5e1' : '#334155',
        lineHeight: 1.6
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogTitle: {
        paddingBottom: "16px"
    }
}));


const ChangesLog = ({ open, onClose }) => {
    const classes = useStyles();
    const theme = useTheme();
    const isLight = theme.palette.type === "light";
    const [expanded, setExpanded] = useState('panel0');

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const formatText = (text) => {
        if (!text) return "";
        return text.split('\n').map((line, lineIndex) => {
            // Split by *bold* markers
            const parts = line.split(/\*([^*]+)\*/g);
            const isBullet = line.trim().startsWith("•") || line.trim().startsWith("🚀") || line.trim().startsWith("👨‍💻");
            
            // If it's a bullet, separate the prefix (bullet + emojis + spaces) from the text for perfect flexbox alignment
            if (isBullet) {
                // Find where the actual text/bold starts (after spaces, bullets, and emojis)
                const match = line.match(/^([^a-zA-Z0-9_*]*)(.*)$/u);
                if (match) {
                    const prefix = match[1];
                    const restOfLine = match[2];
                    const restParts = restOfLine.split(/\*([^*]+)\*/g);
                    
                    return (
                        <div key={lineIndex} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px', textAlign: 'justify' }}>
                            <span style={{ minWidth: '28px', display: 'inline-block', flexShrink: 0 }}>{prefix}</span>
                            <div style={{ flex: 1 }}>
                                {restParts.map((part, partIndex) =>
                                    partIndex % 2 === 1 ? (
                                        <b style={{ color: isLight ? '#00838F' : '#00E5FF' }} key={partIndex}>{part}</b>
                                    ) : (
                                        part
                                    )
                                )}
                            </div>
                        </div>
                    );
                }
            }

            return (
                <div key={lineIndex} style={{ minHeight: '1.2em', textAlign: 'justify', marginBottom: '8px' }}>
                    {parts.map((part, partIndex) =>
                        partIndex % 2 === 1 ? (
                            <b style={{ color: isLight ? '#00838F' : '#00E5FF' }} key={partIndex}>{part}</b>
                        ) : (
                            part
                        )
                    )}
                </div>
            );
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className={`changeslog-dialog ${isLight ? "changeslog-light" : ""}`}>
            <DialogTitle className={classes.dialogTitle}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '10px' }}>🚀</span>
                    {i18n.t("changesLog.title")} - Botmix {changes[0].version}
                </div>
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers style={{ overflow: "hidden", padding: "16px", borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.05)' }}>
                <div className={classes.scrollArea}>
                    <div className={classes.root}>
                        {changes.map((change, index) => (
                        <Accordion
                            key={index}
                            expanded={expanded === `panel${index}`}
                            onChange={handleChange(`panel${index}`)}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMore style={{ color: '#00E5FF' }} />}
                                aria-controls={`panel${index}bh-content`}
                                id={`panel${index}bh-header`}
                            >
                                <Typography className={classes.heading}>
                                    <div className="timeline-dot" style={{ backgroundColor: index === 0 ? '#00E5FF' : '#8B5CF6', boxShadow: index === 0 ? '0 0 10px #00E5FF' : '0 0 10px #8B5CF6' }}></div>
                                    Botmix: {change.version}
                                    {index === 0 && <span className="version-chip">LATEST</span>}
                                </Typography>
                                <Typography className={classes.secondaryHeading}>{change.date}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography component="div" className={classes.content}>
                                    {formatText(i18n.t(`changesLog.logs.${change.key}`))}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                    </div>
                </div>
            </DialogContent>
            <DialogActions style={{ borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.05)', padding: '16px' }}>
                <Button onClick={onClose} color="primary" variant="contained">
                    {i18n.t("changesLog.close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangesLog;
